import { get as _get } from '@lodash';
import Struct from '@models/struct/Struct';
import Match from '@core/models/offer/Match';
import Odd from '@models/offer/Odd';
import Logger from '@loggers/Logger';
import { LiveBettingSpecifiersFormatter } from '@core/services/offer/_private/LiveBettingSpecifiersFormatter';
import { instanceGuard } from '@core/utils/services';
import SingletonError from './errors/SingletonError';
import Singleton from './Singleton';
import ServerConfigProvider from '@core/providers/configuration/ServerConfigProvider';
import PayoutService from '@core/services/betSlip/PayoutService';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';

export class StructManager {
    private static instance: StructManager | undefined;
    private struct: Struct;
    private specifierFormatter: LiveBettingSpecifiersFormatter = new LiveBettingSpecifiersFormatter();

    private constructor(struct: Struct) {
        if (StructManager.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.struct = struct;
    }

    public static getInstance(): StructManager {
        return instanceGuard(StructManager.instance)!;
    }

    public static createInstance({ struct }: { struct: Struct }) {
        if (!StructManager.instance) {
            StructManager.instance = new StructManager(struct);
        }
        return StructManager.instance;
    }

    public denormalizeEvent(event: Match): void {
        event.categoryName = this.getCategoryName(event.categoryId);
        event.sportName = this.getSportName(event.sportId);
        event.tournamentName = this.getTournamentName(event.tournamentId);
        const oddsValid = this.denormalizeOdds(event);
        event.validStruct = oddsValid && !!event.sportName && !!event.categoryName && !!event.tournamentName;
    }

    public denormalizeEvents(events: Match[]): void {
        events.forEach((event) => this.denormalizeEvent(event));
    }

    public hasStruct() {
        return !!this.struct;
    }

    /**
     * Returns true if fields in odd have all been denormalized successfully.
     */
    public denormalizeOdd(event: Match, odd: Odd): boolean {
        odd.marketOriginalName = this.getMarketName(odd.marketId);
        odd.outrightMarketName = odd.marketName;
        odd.marketName = this.specifierFormatter.format(
            this.getMarketName(odd.marketId),
            odd.specifiers!,
            event);
        odd.marketLongName = this.specifierFormatter.format(
            this.getMarketLongName(odd.marketId),
            odd.specifiers!,
            event);
        odd.description = this.specifierFormatter.format(
            this.getOddDescription(odd.id),
            odd.specifiers!,
            event);
        odd.symbol = this.specifierFormatter.format(
            this.getOddSymbol(odd.id),
            odd.specifiers!,
            event);
        // TODO check with filke
        odd.isInPromotion = !!odd.tags && this.promotionalMarketTags!.some(tag => odd.tags!.includes(tag));
        if (
            ServerConfigProvider.getConfig() ?
            ServerConfigProvider.getConfig().betSlip.bonusConfig.superBonusEnabled :
            false
        ) {
            odd.isValidForSuperBonus = PayoutService.getInstance().isOddValidForSuperBonus(odd.tags);
        }

        return !!odd.marketOriginalName && !!odd.marketName && !!odd.marketLongName && !!odd.symbol;
    }

    public denormalizeOdds(event: Match): boolean {
        let isValid = true;
        event.getOdds().forEach((odd: Odd) => {
            const result = this.denormalizeOdd(event, odd);
            if (!result) {
                isValid = false;
            }
        });
        return isValid;
    }

    public getSportName(id: number | undefined): string {
        return this.getValueFromStruct('getSportName', id, `sports[${id}].name`);
    }

    public getCategoryName(id: number | undefined): string {
        return this.getValueFromStruct('getCategoryName', id, `categories[${id}].name`);
    }

    public getMarketName(id: number | undefined): string {
        return this.getValueFromStruct('getMarketName', id, `markets[${id}].name`);
    }

    public getMarketLongName(id: number | undefined): string {
        return this.getValueFromStruct('getMarketLongName', id, `markets[${id}].longName`);
    }

    public getTournamentName(id: number | undefined): string {
        return this.getValueFromStruct('getTournamentName', id, `tournaments[${id}].name`);
    }

    public getOddSymbol(oddId: number | undefined) {
        return this.getValueFromStruct('getOddSymbol', oddId, `oddTypes[${oddId}].symbol`);
    }

    public getOddDescription(oddId: number | undefined) {
        return this.getValueFromStruct('getOddDescription', oddId, `oddTypes[${oddId}].description`);
    }

    public setSpecifierFormatter(formatter: LiveBettingSpecifiersFormatter) {
        this.specifierFormatter = formatter;
    }

    public setStruct(struct: Struct) {
        this.struct = struct;
        delete this._promotionalMarketTags;
    }

    public setMarketGroupInfos(marketGroupInfos: Record<number, MarketGroupInfo>): void {
        this.struct.marketGroupInfos = marketGroupInfos;
    }

    private logIfEmpty(method: string, id: number | undefined, value: string) {
        if (!value) {
            // this.logger.error(new Error(`Empty value in ${method} for ${id}`));
        }
    }

    private logIfUndefined(method: string) {
        Logger.error(new Error(`Undefined id in ${method}`));
    }

    private getValueFromStruct(method: string, id: number | undefined, path: string, defaultValue = '') {
        if (!this.struct) {
            throw new Error('Struct manager not properly configured. Struct is missing.');
        }
        if (!id) {
            this.logIfUndefined(method);
            return '';
        }

        const value = _get(this.struct, path, defaultValue);
        this.logIfEmpty(method, id, value);
        return value;
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete StructManager.instance;
    }

    private _promotionalMarketTags?: string[];
    private get promotionalMarketTags() {
        if (!this._promotionalMarketTags) {
            this._promotionalMarketTags = this.struct.superOffer ? Object.keys(this.struct.superOffer) : [];
        }
        return this._promotionalMarketTags;
    }
}

export interface IStructManager extends StructManager { }

export default StructManager as Singleton<StructManager>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/common/StructManager.ts